import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../../utils/axios-query";
import {
  Participant,
  ParticipantFullSummary,
  ParticipantPreview,
} from "../../types/participant.types";
import { RegistrationRequest } from "../../types/registration.types";
import { PresentationSummary } from "../../types/presentation.types";
import { CongressEventUpdateDto } from "../../types/update.types";

export const participantApi = createApi({
  reducerPath: "participantApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Participants", "Register", "Participant"],
  endpoints: (builder) => ({
    listParticipantsByCongressId: builder.query<Participant[], number>({
      query: (congressId: number) => ({
        url: `participant/congress/${congressId}`,
      }),
      providesTags: ["Participants"],
    }),
    listParticipantPresentation: builder.query<PresentationSummary[], number>({
      query: (participantId: number) => ({
        url: `participant/${participantId}/presentation`,
      }),
    }),
    getParticipantById: builder.query<ParticipantPreview, number>({
      query: (congressId: number) => ({
        url: `participant/${congressId}`,
      }),
      providesTags: ["Participant"],
    }),
    getParticipantSummaryById: builder.query<
      ParticipantFullSummary,
      number | null
    >({
      query: (congressId: number | null) => ({
        url: `participant/${congressId}/preview`,
        enabled: congressId !== null,
      }),
      providesTags: ["Register"],
    }),
    register: builder.mutation<ParticipantFullSummary, RegistrationRequest>({
      query: (registration: RegistrationRequest) => {
        const valuesByFieldId: any = {};
        registration.participantRegister.valuesByFieldId.forEach(
          (value: string | number, key: number) => {
            valuesByFieldId[key] = value;
          },
        );
        const mailingFields: any = {};
        registration.address?.valuesByFieldId.forEach(
          (value: string | number, key: number) => {
            mailingFields[key] = value;
          },
        );
        const payload = {
          ...registration,
          participantRegister: {
            ...registration.participantRegister,
            valuesByFieldId,
          },
          address: {
            ...registration.address,
            valuesByFieldId: mailingFields,
          },
          ...(registration.payment && { payment: registration.payment }),
        };

        if (registration.payment) {
          const billingFields: any = {};
          registration.payment.valuesByFieldId.forEach(
            (value: string | number, key: number) => {
              billingFields[key] = value;
            },
          );
          payload.payment = {
            ...registration.payment,
            valuesByFieldId: billingFields,
          };
        }
        return {
          url: "congress-event/register-to-event",
          data: payload,
          method: "POST",
        };
      },
      invalidatesTags: ["Register", "Participants"],
    }),
    downloadExcel: builder.mutation<Blob, number | string>({
      query: (congressId: number | string) => ({
        url: `excel-export/${congressId}`,
        method: "GET",
        headers: {
          Accept: "application/octet-stream",
        },
        responseType: "blob",
      }),
      invalidatesTags: ["Participants"],
    }),
    downloadExcelPresentations: builder.mutation<Blob, number | string>({
      query: (congressId: number | string) => ({
        url: `excel-export/presentations/${congressId}`,
        method: "GET",
        headers: {
          Accept: "application/octet-stream",
        },
        responseType: "blob",
      }),
      // invalidatesTags: ["Participants"],
    }),
    resendEmail: builder.mutation<void, number | string>({
      query: (participantId: number) => ({
        url: `participant/${participantId}/resend-email`,
        method: "GET",
      }),
      invalidatesTags: ["Participants"],
    }),
    updateParticipant: builder.mutation<
      ParticipantFullSummary,
      { participantId: number; data: RegistrationRequest }
    >({
      query: ({ participantId, data }) => {
        const valuesByFieldId: any = {};
        data.participantRegister.valuesByFieldId.forEach(
          (value: string | number, key: number) => {
            valuesByFieldId[key] = value;
          },
        );
        const mailingFields: any = {};
        data.address?.valuesByFieldId.forEach(
          (value: string | number, key: number) => {
            mailingFields[key] = value;
          },
        );
        const payload = {
          ...data,
          participantRegister: {
            ...data.participantRegister,
            valuesByFieldId,
          },
          address: {
            ...data.address,
            valuesByFieldId: mailingFields,
          },
          ...(data.payment && { payment: data.payment }),
        };

        if (data.payment) {
          const billingFields: any = {};
          data.payment.valuesByFieldId.forEach(
            (value: string | number, key: number) => {
              billingFields[key] = value;
            },
          );
          payload.payment = {
            ...data.payment,
            valuesByFieldId: billingFields,
          };
        }

        return {
          url: `participant/${participantId}`,
          method: "PUT",
          data: payload,
        };
      },
      invalidatesTags: ["Participants", "Register"],
    }),
    updateRegistration: builder.mutation<
      ParticipantFullSummary,
      CongressEventUpdateDto
    >({
      query: (updateDto: CongressEventUpdateDto) => {
        return {
          url: "congress-event/update-registration",
          method: "PUT",
          data: updateDto,
        };
      },
      invalidatesTags: ["Participants", "Register"],
    }),
    finalizeParticipant: builder.mutation<void, number>({
      query: (participantId: number) => ({
        url: `participant/${participantId}/finalize`,
        method: "POST",
      }),
      invalidatesTags: ["Participants", "Register"],
    }),
    finalizeAndSendEmail: builder.mutation<void, number>({
      query: (participantId: number) => ({
        url: `participant/${participantId}/finalize-and-send-email`,
        method: "POST",
      }),
      invalidatesTags: ["Participants", "Register"],
    }),
  }),
});

export const {
  useListParticipantsByCongressIdQuery,
  useRegisterMutation,
  useListParticipantPresentationQuery,
  useGetParticipantByIdQuery,
  useGetParticipantSummaryByIdQuery,
  useDownloadExcelMutation,
  useResendEmailMutation,
  useDownloadExcelPresentationsMutation,
  useUpdateParticipantMutation,
  useUpdateRegistrationMutation,
  useFinalizeParticipantMutation,
  useFinalizeAndSendEmailMutation,
} = participantApi;
